'use client';

import React, { useState, useEffect } from 'react';
import { Gift, ChevronRight, ChevronLeft, Coffee, Image, Frame } from 'lucide-react';
import { MerchandiseProductCard } from './MerchandiseProductCard';
import { MerchandiseCheckoutModal } from './MerchandiseCheckoutModal';
import { getPublicImageUrl } from '@/utils/images/urlConverter';
import { MerchandiseProduct, MerchandiseCategory } from '@/types/merchandise';
import { detectCountry } from '@/utils/countryDetection';
import { useSiteFlags } from '@/context/SiteFlagsContext';

interface MerchandiseUpsellProps {
  coverImageUrl: string;
  childName?: string; // Optional - will use fallback if not provided
  bookId?: string;
  placement?: 'cover-preview' | 'post-purchase' | 'free-cover';
  onOrderSuccess?: (orderId: string) => void;
}

interface LocalizedPrice {
  amount: number;
  currency: string;
  symbol: string;
  formatted: string;
  isApproximate: boolean;
}

interface ProductWithPricing extends MerchandiseProduct {
  pricing: {
    productPrice: number;
    shippingCost: number;
    total: number;
    formatted: {
      productPrice: string;
      shippingCost: string;
      total: string;
    };
    localized?: {
      productPrice: LocalizedPrice;
      shippingCost: LocalizedPrice;
      total: LocalizedPrice;
      currency: string;
      countryCode: string;
    };
  };
  estimatedDelivery: { min: number; max: number };
}

// Merch is behind the MERCH_ENABLED kill switch (default OFF). Gating here as well as at
// the call sites guarantees no entry point can render it (or fetch products) while off.
export const MerchandiseUpsell: React.FC<MerchandiseUpsellProps> = (props) => {
  const { merchEnabled } = useSiteFlags();
  if (!merchEnabled) return null;
  return <MerchandiseUpsellContent {...props} />;
};

const MerchandiseUpsellContent: React.FC<MerchandiseUpsellProps> = ({
  coverImageUrl,
  childName = 'Hero', // Fallback for API/analytics only, not displayed in UI
  bookId,
  placement = 'cover-preview',
  onOrderSuccess,
}) => {
  const [products, setProducts] = useState<ProductWithPricing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductWithPricing | null>(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<MerchandiseCategory | 'all'>('all');

  // Convert to public URL
  const publicImageUrl = getPublicImageUrl(coverImageUrl);

  // Fetch products on mount with country detection
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);

        // Detect user's country for localized pricing
        const detectedCountry = await detectCountry();
        const countryCode = detectedCountry || 'US';

        const response = await fetch(`/api/products/merchandise/catalog?country=${countryCode}`);

        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();
        setProducts(data.products);
      } catch (err) {
        console.error('Error fetching merchandise:', err);
        setError('Unable to load products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products by category
  const filteredProducts =
    activeCategory === 'all'
      ? products
      : products.filter((p) => p.category === activeCategory);

  // Handle product selection
  const handleProductSelect = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setIsCheckoutOpen(true);
    }
  };

  // Handle checkout success
  const handleCheckoutSuccess = (orderId: string) => {
    setIsCheckoutOpen(false);
    setSelectedProduct(null);
    if (onOrderSuccess) {
      onOrderSuccess(orderId);
    }
  };

  if (error) {
    return null; // Silently fail - merchandise is optional
  }

  if (isLoading) {
    return (
      <div className="mt-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-4">
          <Gift className="h-5 w-5 text-purple-600" />
          <span className="text-gray-600">Loading personalized products...</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-white rounded-xl h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return null; // No products available
  }

  return (
    <>
      <div className="mt-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-bold text-gray-900">
              {placement === 'cover-preview' || placement === 'free-cover'
                ? 'Love the cover? Get it on merch!'
                : 'Take the adventure home!'}
            </h3>
          </div>
        </div>

        <p className="text-gray-600 mb-4">
          {placement === 'cover-preview' || placement === 'free-cover'
            ? 'Turn this beautiful cover into a keepsake that lasts forever.'
            : 'Celebrate your purchase with personalized merchandise featuring your unique book cover.'}
        </p>

        {/* Category Filter */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeCategory === 'all'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 hover:bg-purple-100'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveCategory('mug')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              activeCategory === 'mug'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 hover:bg-purple-100'
            }`}
          >
            <Coffee className="h-3.5 w-3.5" />
            Mugs
          </button>
          <button
            onClick={() => setActiveCategory('poster')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              activeCategory === 'poster'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 hover:bg-purple-100'
            }`}
          >
            <Image className="h-3.5 w-3.5" />
            Posters
          </button>
          <button
            onClick={() => setActiveCategory('canvas')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              activeCategory === 'canvas'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 hover:bg-purple-100'
            }`}
          >
            <Frame className="h-3.5 w-3.5" />
            Canvas
          </button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => (
            <MerchandiseProductCard
              key={product.id}
              id={product.id}
              name={product.name}
              category={product.category}
              description={product.description}
              previewImageUrl={publicImageUrl}
              pricing={product.pricing}
              estimatedDelivery={product.estimatedDelivery}
              onSelect={handleProductSelect}
              isSelected={selectedProduct?.id === product.id}
            />
          ))}
        </div>

        {/* Footer */}
        <p className="text-xs text-gray-500 mt-4 text-center">
          High-quality print-on-demand products. Ships worldwide.
          Each item is made just for you.
        </p>
      </div>

      {/* Checkout Modal */}
      {selectedProduct && (
        <MerchandiseCheckoutModal
          isOpen={isCheckoutOpen}
          onClose={() => {
            setIsCheckoutOpen(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
          coverImageUrl={publicImageUrl}
          childName={childName}
          bookId={bookId}
          onSuccess={handleCheckoutSuccess}
        />
      )}
    </>
  );
};

export default MerchandiseUpsell;
